import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {get, patch, post} from '../../core/http';
import {API} from '../../core/constants';
import CoverImage from "../CoverImage";
import {docId} from "../../core/Firebase";
import Modal from '../ui/Modal';

const ArticleForm = ({article, onFormSubmit, onCancel}) => {
    const [id, setId] = useState('');

    const [eventOptions, setEventOptions] = useState();
    const [external, setExternal] = useState(true);
    const [imageUrl, setImageUrl] = useState('');

    useEffect(() => {
        if (!article?.id) {
            setId(docId());
        } else {
            setImageUrl(article.cover);
            setId(article.id);
        }
    }, [article]);

    const {
        register,
        handleSubmit,
        formState: {errors},
    } = useForm({
        defaultValues: {
            ...article,
            external: article?.external || external
        },
    });

    useEffect(() => {
        const getEventOptions = async () => {
            const result = await get(`${API}/event-options`);

            if (result.body) {
                setEventOptions(result.body);
            }
        };

        getEventOptions();
    }, [article]);

    const onSubmit = async (payload) => {
        try {
            let result;

            if (article?.id) {
                result = await patch(`${API}/articles/${article.id}`).send({
                    ...payload,
                    cover: imageUrl
                });

                if (result.body) {
                    console.log(result.body);
                }
            } else {
                result = await post(`${API}/articles`).send({
                    ...payload,
                    cover: imageUrl
                });
            }

            onFormSubmit(result.body);
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <Modal title={article ? article.title : 'Add new article'} description={article ? 'Editing article' : null} onClose={onCancel} wide>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                {imageUrl ? (
                    <img className="rounded-md max-h-40 object-cover" src={imageUrl || article.cover} alt="" />
                ) : (
                    <CoverImage type={"article"} id={id} onUploadComplete={setImageUrl} />
                )}

                <label className="block">
                    <span className="label-text text-xs font-semibold">Title</span>
                    <input
                        className="input input-bordered input-sm w-full mt-1"
                        {...register('title', {required: 'Title is required'})}
                        placeholder="Title"
                    />
                    {errors.title && <p className="text-error text-xs mt-1">{errors.title.message}</p>}
                </label>

                {article?.aiGenerated && (
                    <div className="p-2 border border-accent/40 rounded-md bg-accent/10 text-sm">
                        <p className="font-semibold text-accent">AI-imported</p>
                        {article?.aiMatchReason && <p>Event match: {article.aiMatchReason}</p>}
                        {!article?.eventId && <p>No confident event match — assign one below before publishing.</p>}
                    </div>
                )}

                <label className="block">
                    <span className="label-text text-xs font-semibold">Summary</span>
                    <textarea
                        className="textarea textarea-bordered w-full mt-1"
                        {...register('summary')}
                        placeholder="Summary"
                        rows={3}
                    />
                </label>

                <label className="block">
                    <span className="label-text text-xs font-semibold">Event</span>
                    <select
                        className="select select-bordered select-sm w-full mt-1" {...register('eventId', {required: 'Shared is required'})}>
                        <option value="">Select...</option>
                        {eventOptions?.map((option) => (
                            <option key={option.id} selected={article?.eventId === option.id} value={option.id}>
                                {option.title}
                            </option>
                        ))}
                    </select>
                    {errors.status && <p className="text-error text-xs mt-1">{errors.status.message}</p>}
                </label>

                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" className="checkbox checkbox-sm" name={"external"}
                           {...register('external')}
                           onChange={e => setExternal(e.target.checked)}
                    />
                    External?
                </label>

                {external ? (
                    <label className="block">
                        <span className="label-text text-xs font-semibold">URL</span>
                        <input
                            className="input input-bordered input-sm w-full mt-1"
                            {...register('url', {required: 'URL is required'})}
                            placeholder="URL"
                        />
                        {errors.url && <p className="text-error text-xs mt-1">{errors.url.message}</p>}
                    </label>
                ) : (
                    <label className="block">
                        <span className="label-text text-xs font-semibold">Content</span>
                        <textarea
                            className="textarea textarea-bordered w-full mt-1"
                            {...register('content', {required: 'Content is required'})}
                            placeholder="Content"
                            rows={6}
                        />
                        {errors.content && <p className="text-error text-xs mt-1">{errors.content.message}</p>}
                    </label>
                )}

                <label className="block">
                    <span className="label-text text-xs font-semibold">Date</span>
                    <input
                        className="input input-bordered input-sm w-full mt-1"
                        type="text"
                        {...register('date', {required: 'Date is required'})}
                        placeholder="Date"
                    />
                    {errors.date && <p className="text-error text-xs mt-1">{errors.date.message}</p>}
                </label>

                <label className="block">
                    <span className="label-text text-xs font-semibold">Status</span>
                    <select
                        className="select select-bordered select-sm w-full mt-1" {...register('status', {required: 'Status is required'})}>
                        <option value="">Select...</option>
                        <option value="draft">Draft</option>
                        <option value="publish">Publish</option>
                        <option value="private">Private</option>
                    </select>
                    {errors.status && <p className="text-error text-xs mt-1">{errors.status.message}</p>}
                </label>

                <label className="block">
                    <span className="label-text text-xs font-semibold">Tags</span>
                    <input
                        className="input input-bordered input-sm w-full mt-1"
                        {...register('tags', {required: 'Tags are required'})}
                        placeholder="Tags"
                    />
                    {errors.tags && <p className="text-error text-xs mt-1">{errors.tags.message}</p>}
                </label>

                <div className="flex gap-2 pt-2">
                    <button type="button" className="btn btn-ghost btn-sm flex-1" onClick={() => onCancel()}>
                        Cancel
                    </button>
                    <input className="btn btn-primary btn-sm flex-1" type="submit" value={article ? 'Save' : 'Create'} />
                </div>
            </form>
        </Modal>
    );
};

export default ArticleForm;
